import type { LogBasedError, TriageError } from './types';
import { DEFAULT_RUNBOOK_LINK, runbookLink } from './types';

/**
 * These are produced by our application and should be investigated/fixed.
 * Once the added Linear ticket is done, these should be removed from here.
 */
export const webViteTriageErrors: TriageError[] = [
  {
    messageContains: 'Unhandled error in session client factory',
    threshold: 15,
    priority: 4,
    ticket: 'ENG-78',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'The operation failed for an unknown transient reason',
    threshold: 5,
    priority: 4,
    ticket: 'ENG-1054',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Failed to write audio packet to local file',
    threshold: 10,
    priority: 4,
    ticket: 'ENG-1055',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'The object can not be found here',
    threshold: 5,
    priority: 4,
    ticket: 'ENG-1084',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Failed to upload file',
    threshold: 5,
    priority: 4,
    ticket: 'ENG-1338',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Recording Exception: Cannot write to stream',
    messageContains: 'Recording Exception',
    jsonContains: {
      error: 'Cannot?write?to*',
    },
    threshold: 3,
    priority: 4,
    ticket: 'ENG-1830',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Recording Exception: State changed since read from disk',
    messageContains: 'Recording Exception',
    jsonContains: {
      error: '*state?had?changed?since?it?was?read?from?disk*',
    },
    threshold: 3,
    priority: 4,
    ticket: 'ENG-1830',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Recording Exception: Storage quota exceeded',
    messageContains: 'Recording Exception',
    jsonContains: {
      error: '*cause?the?application?to?exceed?its?storage?quota*',
    },
    threshold: 3,
    priority: 4,
    ticket: 'ENG-1830',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Recording Exception: Key already exists',
    messageContains: 'Recording Exception',
    jsonContains: {
      error: '*Key?already?exists?in?the?object?store*',
    },
    threshold: 3,
    priority: 4,
    ticket: 'ENG-1830',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Recording Exception: Issues with OPFS/IndexedDB',
    messageContains: 'Recording Exception',
    jsonContains: {
      error: '"Connection is lost"',
    },
    threshold: 5,
    priority: 4,
    ticket: 'ENG-1909',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    // Signature of a session that captured ZERO audio (dead encoder worker):
    // spiked 22–57/day in the hours after every web deploy that stranded
    // stale tabs, against a 1–9/day baseline — invisible for a month because
    // the log is warn-level and SLA-excluded. The 4h window matches the
    // post-deploy burst shape. No `status`, so a later log-level change
    // cannot blind the monitor.
    title: 'Segments finalized with no local audio file',
    messageContains: 'Segment finalized with no local audio file',
    threshold: 10,
    priority: 4,
    evaluationWindow: '4h',
    ticket: 'ENG-5284',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    // Mirrors the extension entry (extension.errors.ts) — the log comes from
    // shared `machine-common`, so both clients emit it. See there for why
    // `status` is pinned against the convention.
    status: 'error',
    title: 'Recording finalize timed out',
    messageContains: 'Recording finalize timed out',
    threshold: 10,
    priority: 4,
    evaluationWindow: '4h',
    ticket: 'ENG-5457',
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    // Mirrors the extension entry (extension.errors.ts) — the log comes from
    // shared `machine-common`, so both clients emit it.
    title: 'Encoder worker stop handshake timed out',
    messageContains: 'Encoder worker stop handshake timed out',
    threshold: 1,
    priority: 4,
    ticket: 'ENG-5741',
    notionLink: runbookLink('aba22b93705f42a8a3f24ad50b8fa6b0'),
  },
];

/**
 * These are produced by our application, but are not going to be fixed
 * either because they don't have a fix or because we have chosen not to.
 */
export const webViteKnownErrors: LogBasedError[] = [
  {
    messageContains: 'Import EHR data - FAILED UNEXPECTEDLY',
    title: 'Import EHR Data failure',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'No summary available to display to user. Investigate immediately.',
    messageContains: 'No summary available to display to user',
    threshold: 3,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title:
      'MediaRecorder health check warning indicates audio not being properly received even though recording is active. Validate appointment audio input.',
    messageContains: 'MediaRecorder in recording state but not healthy',
    threshold: 1,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title:
      'Transcription state machine stopped in less than 1 second, validate appointment audio',
    messageContains: 'Transcription stopped in less than 1 second',
    threshold: 1,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Could not verify JWT',
    threshold: 10,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Error while reporting local file stats',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains:
      'Failed to upload file: File upload onload received non-200 status. Got status 400',
    threshold: 10,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains:
      'Failed to upload file: Failed to upload file due to network error',
    threshold: 10,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Failed to close local audio file',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Audio file system write error',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    // ENG-3775: transient OPFS NotReadableError when hashing an audio batch
    // chunk. Self-healing; calculateFileMd5 returns '' and the upload flow
    // retries. Defence-in-depth alongside the log-level downgrade to warn.
    messageContains: 'MD5 hash calculation failed',
    threshold: 10,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Failed to send challenger email',
    threshold: 1,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Recording Exception: Error getting audio stream',
    messageContains: 'Recording Exception',
    jsonContains: {
      error: '*error?occurred?while?getting?stream?from?audio?input?device*',
    },
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Reached max number of infos by minute',
    threshold: 1,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    messageContains: 'Task exceeded max retries, giving up',
    title: 'Appointment task failed after max retries',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Recording Exception (catch-all)',
    messageContains: 'Recording Exception',
    threshold: 10,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Intercom failed to initialize',
    messageContains: 'Intercom failed to initialize',
    threshold: 1,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Error consuming magic link',
    messageContains: 'Error consuming magic link',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Failed to create appointment (network error)',
    messageContains:
      'Failed to create appointment. Please check your internet connection and try again.',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Error fetching user-registration should-show (transient network)',
    messageContains: 'Error fetching user-registration should-show',
    threshold: 5,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Sync to EHR: extension connection lost',
    messageContains: 'Error syncing to EHR',
    jsonContains: {
      errorMessage:
        '"Lost connection with Eluve extension. Page refresh needed"',
    },
    threshold: 3,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title: 'Sync to EHR: extension sync timeout',
    messageContains: 'Error syncing to EHR',
    jsonContains: {
      error: '*Timeout?for?syncToEhr?after*ms',
    },
    threshold: 3,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
  {
    title:
      'React error boundary triggered by transient network error (Failed to fetch / Load failed)',
    messageContains: 'React render error caught by ErrorFallback',
    jsonContains: {
      // Chromium throws 'Failed to fetch'; Safari throws 'Load failed'. Both
      // are downgraded to warn at the call site, so the spike monitor must
      // match either to keep covering Safari users during sustained outages.
      'error.message': '(*Failed?to?fetch* OR *Load?failed*)',
    },
    threshold: 10,
    priority: 4,
    notionLink: DEFAULT_RUNBOOK_LINK,
  },
];
